import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'
import { useDispatch, useSelector } from 'react-redux'

import SupportChatModal from 'components/modals/supportChatModal'
import { RootState } from 'store/redux'
import { toggleSupportModal } from 'store/redux/sidebar'

interface IModalProps {
    show: boolean
    onHide: () => void
}

/** Which of the five routes out of this modal the user took. */
type TChannel = 'call' | 'faq' | 'eitaa' | 'telegram' | 'bot'

// Ten screens render their own <SupportModal> off one redux flag, so on the
// routes where a page and its layout both do it two instances see the same
// flip. One human action, one event: whichever instance gets there first
// reports, and closing arms the next one.
let reportedOpen = false

const SupportModal = ({ show, onHide }: IModalProps) => {
    // The chatbot lives here rather than in the layout so this modal owns the
    // whole "contact support" flow: it can hand off to the bot and the bot can
    // hand back when a human is needed.
    const [showChat, setShowChat] = useState(false)
    const dispatch = useDispatch()
    const source = useSelector(
        (state: RootState) => state.sideBarControler.supportModalSource
    )

    useEffect(() => {
        if (!show) {
            reportedOpen = false
            return
        }
        if (reportedOpen) return
        reportedOpen = true
        posthog.capture('support_options_opened', { source })
    }, [show, source])

    /**
     * Count which way out of this modal the user took.
     *
     * This used to also POST their name and phone number to
     * `support.gethamyan.ir/webhook/monitoring-users-messages` so support would
     * know somebody had asked for help. **That host has never resolved.** The
     * call was added 2024-02-26 and every one of them has failed since; once a
     * `.catch()` was finally added the failures showed up 1:1 with the taps.
     * In the two weeks PostHog has covered, 71 taps from 24 different people
     * went nowhere — one of them a fund owner who tapped support seven times.
     *
     * It is gone rather than repointed because it carried nothing PostHog does
     * not already have: `distinct_id` on this event IS the phone number, and
     * the name is a person property. What was missing was push, not data, and
     * push now exists where it is actually actionable — the backend messages
     * the support group when the bot escalates a question it cannot answer.
     * Adding "someone tapped تماس تلفنی" to that group would dilute the one
     * signal that means a person genuinely needs to act.
     */
    const recordChoice = (channel: TChannel) => {
        posthog.capture('support_channel_chosen', { channel })
    }

    return (
        <>
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className=" w-full text-center py-4 text-xs">
                <p className=" mb-3 px-3">
                    از ساعت 10:30 الی 15 از طریق تماس تلفنی و از ساعت 9 تا 17 در پیام رسان‌ها، پاسخ‌گوی شما عزیزان هستیم.
                </p>
                <hr className=" mb-3" />
                <a
                    className=" text-green-1 my-3 no-underline"
                    onClick={() => {
                        recordChoice('call')
                        onHide()
                    }}
                    href="tel:02191070174"
                >
                    تماس تلفنی
                </a>
                <hr className=" mt-3" />
                {/* Was a link to hamyanapp.com/blog/faq, which 404s — the
                    blog archive is gone. Six people tapped it in two weeks and
                    every one of them landed on a not-found page, on the screen
                    you reach when something is already wrong.

                    It opens the assistant instead: FAQ-shaped questions
                    («کارمزد چقدره؟», «چطور قسطم را بدهم؟») are exactly what it
                    answers well, and it now hands over a real link to the page
                    rather than describing a route to it.

                    The navigation used to sit in `onClickCapture`, which fires
                    BEFORE `onClick` — so the page often left before the
                    analytics event was captured, and the faq count is an
                    undercount. */}
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        recordChoice('faq')
                        onHide()
                        setShowChat(true)
                        posthog.capture('support_bot_opened', {
                            source: 'faq',
                        })
                    }}
                >
                    سوال‌های متداول{' '}
                    <span className=" text-[10px] text-gray-400">
                        (پاسخ از پشتیبان هوشمند)
                    </span>
                </p>
                <hr />
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        recordChoice('eitaa')
                        onHide()
                        window.location.replace('https://eitaa.com/Hamyanapp')
                    }}
                >
                    پیام در ایتا
                </p>
                <hr />
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        // The webhook this used to feed wanted the label
                        // 'whatsapp' long after the option became Telegram. The
                        // webhook is gone, so the lie can go with it.
                        recordChoice('telegram')
                        onHide()
                        window.location.replace(
                            'https://t.me/hamyanapp'
                        )
                    }}
                >
                    پیام در تلگرام
                </p>
                <hr />
                {/* Deliberately the LAST option, not the first: this is a trial
                    and the human routes should stay the obvious path until the
                    team has used it enough. Moving it up is a one-line change. */}
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        recordChoice('bot')
                        onHide()
                        setShowChat(true)
                        posthog.capture('support_bot_opened', {
                            source: 'menu',
                        })
                    }}
                >
                    پرسش از پشتیبان هوشمند{' '}
                    <span className=" text-[10px] text-gray-400">
                        (آزمایشی)
                    </span>
                </p>
                <hr />
                <p className=" mt-4" onClick={onHide}>
                    انصراف
                </p>
            </div>
        </Modal>

        <SupportChatModal
            show={showChat}
            onHide={() => setShowChat(false)}
            onWantsHuman={() => {
                setShowChat(false)
                dispatch(
                    toggleSupportModal({
                        isOpenSupportModal: true,
                        source: 'bot_escalation',
                    })
                )
            }}
        />
        </>
    )
}

export default SupportModal
